<script setup lang="ts">
import { type HTMLAttributes, type Ref, ref } from "vue";
import type { ProductResponse } from "../../../responses/ProductResponse";
import { Money } from "../../../utils/money";
import { tw } from "../../../utils/tailwind";
import { useFavoriteStore } from "../../stores/favorite";
import { useProductStore } from "../../stores/product";
import ProductQuickbuy from "./ProductQuickbuy.vue";

interface Props {
    product: ProductResponse;
    class?: HTMLAttributes["class"];
}

const props = defineProps<Props>();
const showQuickbuy: Ref<boolean> = ref(false);
const productStore = useProductStore();
const favoriteStore = useFavoriteStore();

const emit = defineEmits<(e: "quickBuy", product: ProductResponse) => void>();

const loggedIn: Ref<boolean> = ref(window.logged_in ?? false);
const isFavorite: Ref<boolean> = ref(props.product.is_favorite);

const toggleFavorite = () => {
    favoriteStore
        .toggleFavorite(props.product.id)
        .then(() => {
            isFavorite.value = !isFavorite.value;
        })
        .catch(console.error);
};
const quickBuy = () => {
    showQuickbuy.value = true;
};
</script>
<template>
    <div
        :class="tw('group relative flex flex-col overflow-hidden border border-secondary-200 bg-white hover:border-secondary-300 transition duration-150', props.class)"
    >
        <div class="aspect-w-3 aspect-h-4 group-hover:opacity-75 sm:aspect-none">
            <a :href="`/${product.slug}/`">
                <img :src="product.thumbnail?.image_url"
                     class="h-full w-full aspect-square object-contain object-center sm:h-full sm:w-full"
                     :alt="product.thumbnail?.alt_text">
            </a>
        </div>
        <div class="flex flex-1 flex-col items-start space-y-2 p-4">
            <h3 class="text-base lg:text-lg font-bold text-primary-900 line-clamp-2">
                <a :href="`/${product.slug}/`">
                    {{ product.name }}
                </a>
            </h3>
            <span class="text-sm font-normal text-card-muted-foreground w-full" v-if="product.alternative_name">{{ product.alternative_name }}</span>
            <div class="flex flex-row absolute top-2 left-4 gap-2" v-if="product.product_labels?.length">
                <span class="text-sm leading-5 px-3 py-1 font-semibold bg-primary text-white rounded-full z-5" v-for="label in product.product_labels" v-text="label.label"></span>
            </div>
            <span class="text-sm leading-5 px-2 py-2 font-semibold bg-white/70 text-primary rounded-lg absolute top-4 right-4 cursor-pointer" v-if="loggedIn" @click="toggleFavorite">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" :fill="isFavorite ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mt-0.5 lucide lucide-heart"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path></svg>
            </span>
        </div>
        <div class="py-2 px-2 flex flex-row justify-between border-t" v-if="product.price">
            <p class="text-base ml-1 font-medium text-primary-900 flex items-center">
                <span>{{ Money.format(product.price.price_incl, product.price.currency) }}</span>
                <span v-if="product.price?.price_incl !== null && product.price.price_incl < product.price.from_price_incl" class="font-normal text-sm text-base-200 line-through ml-2">{{ Money.format(product.price.from_price_incl, product.price.currency) }}</span>
            </p>

            <button
                class="text-sm leading-5 px-2 py-2 font-semibold bg-primary text-white rounded-lg"
                @click="quickBuy">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
            </button>
        </div>
    </div>

    <ProductQuickbuy v-if="showQuickbuy" :product="product" @close="showQuickbuy = false"></ProductQuickbuy>
</template>
